/**
 * Appwrite Client Configuration
 *
 * This file initializes and exports the Appwrite client, account, and databases services.
 * It replaces the previous Supabase client (src/lib/supabase.js).
 */

import { Client, Account, Databases, Query, ID } from 'appwrite';

// Validate environment variables
if (!import.meta.env.VITE_APPWRITE_ENDPOINT || !import.meta.env.VITE_APPWRITE_PROJECT_ID) {
  console.error('Missing Appwrite environment variables!');
  console.error('VITE_APPWRITE_ENDPOINT:', import.meta.env.VITE_APPWRITE_ENDPOINT);
  console.error('VITE_APPWRITE_PROJECT_ID:', import.meta.env.VITE_APPWRITE_PROJECT_ID);
  console.error('Please check your .env file and restart the dev server.');
  throw new Error('Missing Appwrite environment variables. Please check your .env file and restart the dev server.');
}

// Initialize the Appwrite client
const client = new Client()
  .setEndpoint(import.meta.env.VITE_APPWRITE_ENDPOINT) // Toronto region endpoint
  .setProject(import.meta.env.VITE_APPWRITE_PROJECT_ID); // Your project ID

// Initialize services
export const account = new Account(client);
export const databases = new Databases(client);

// Export client for realtime subscriptions
export { client };

// Export Query helper for building database queries
export { Query };

// Export ID helper for generating unique IDs
export { ID };

// Database configuration
export const DATABASE_ID = 'ln2_inventory_db';

// Collection IDs
export const COLLECTIONS = {
  USER_PROFILES: 'user_profiles',
  DEWARS: 'dewars',
  NITROGEN_LEVEL_HISTORY: 'nitrogen_level_history',
  RACKS: 'racks',
  BOXES: 'boxes',
  SAMPLES: 'samples',
  USER_SETTINGS: 'user_settings'
};

/**
 * Helper function to handle Appwrite errors consistently
 * @param {Error} error - The error object from Appwrite
 * @returns {Object} Formatted error object
 */
export const handleAppwriteError = (error) => {
  console.error('Appwrite Error:', error);

  return {
    message: error.message || 'An error occurred',
    code: error.code || 500,
    type: error.type || 'unknown_error'
  };
};

/**
 * Helper function to convert Appwrite document to camelCase
 * Appwrite uses $id, $createdAt, etc. - we'll keep those but convert our custom fields
 * @param {Object} doc - Appwrite document
 * @returns {Object} Converted document
 */
export const convertDocumentToCamelCase = (doc) => {
  if (!doc) return null;

  const converted = { ...doc };

  // Convert snake_case fields to camelCase
  const conversions = {
    user_id: 'userId',
    full_name: 'fullName',
    email_verified: 'emailVerified',
    approved_by: 'approvedBy',
    approved_at: 'approvedAt',
    liquid_nitrogen_level: 'liquidNitrogenLevel',
    dewar_id: 'dewarId',
    previous_level: 'previousLevel',
    checked_by: 'checkedBy',
    checked_at: 'checkedAt',
    rack_number: 'rackNumber',
    rack_id: 'rackId',
    box_type: 'boxType',
    max_capacity: 'maxCapacity',
    occupied_count: 'occupiedCount',
    box_id: 'boxId',
    sample_id: 'sampleId',
    row_index: 'rowIndex',
    col_index: 'colIndex',
    freeze_date: 'freezeDate',
    frozen_by: 'frozenBy',
    passage_number: 'passageNumber',
    cap_color: 'capColor',
    original_sample_id: 'originalSampleId',
    email_notifications: 'emailNotifications',
    temperature_alerts: 'temperatureAlerts',
    sample_movement_alerts: 'sampleMovementAlerts',
    session_timeout: 'sessionTimeout',
    ip_whitelist: 'ipWhitelist',
    auto_backup: 'autoBackup',
    data_retention_policy: 'dataRetentionPolicy',
    export_data: 'exportData',
    dark_mode: 'darkMode',
    compact_view: 'compactView',
    show_grid_lines: 'showGridLines'
  };

  Object.keys(conversions).forEach(snakeKey => {
    if (snakeKey in converted) {
      converted[conversions[snakeKey]] = converted[snakeKey];
      delete converted[snakeKey];
    }
  });

  return converted;
};

/**
 * Helper function to convert camelCase object to snake_case for Appwrite
 * @param {Object} data - Data object with camelCase keys
 * @returns {Object} Converted object with snake_case keys
 */
export const convertDataToSnakeCase = (data) => {
  if (!data) return null;

  const converted = { ...data };

  const conversions = {
    userId: 'user_id',
    fullName: 'full_name',
    emailVerified: 'email_verified',
    approvedBy: 'approved_by',
    approvedAt: 'approved_at',
    liquidNitrogenLevel: 'liquid_nitrogen_level',
    dewarId: 'dewar_id',
    previousLevel: 'previous_level',
    checkedBy: 'checked_by',
    checkedAt: 'checked_at',
    rackNumber: 'rack_number',
    rackId: 'rack_id',
    boxType: 'box_type',
    maxCapacity: 'max_capacity',
    occupiedCount: 'occupied_count',
    boxId: 'box_id',
    sampleId: 'sample_id',
    rowIndex: 'row_index',
    colIndex: 'col_index',
    freezeDate: 'freeze_date',
    frozenBy: 'frozen_by',
    passageNumber: 'passage_number',
    capColor: 'cap_color',
    originalSampleId: 'original_sample_id',
    emailNotifications: 'email_notifications',
    temperatureAlerts: 'temperature_alerts',
    sampleMovementAlerts: 'sample_movement_alerts',
    sessionTimeout: 'session_timeout',
    ipWhitelist: 'ip_whitelist',
    autoBackup: 'auto_backup',
    dataRetentionPolicy: 'data_retention_policy',
    exportData: 'export_data',
    darkMode: 'dark_mode',
    compactView: 'compact_view',
    showGridLines: 'show_grid_lines'
  };

  Object.keys(conversions).forEach(camelKey => {
    if (camelKey in converted) {
      converted[conversions[camelKey]] = converted[camelKey];
      delete converted[camelKey];
    }
  });

  return converted;
};

export default client;
